import { useState } from "react";
import { TabBar } from "../../components/TabBar";
import { Button } from "../../components/Button";
import { QuestionText } from "../../components/QuestionText";

interface TutorScreenProps {
  context: { subject: string; source: string; questionNumber: number; summary: string };
  question: string;
  answer: string;
  suggestions?: string[] | undefined;
  onAsk?: ((question: string) => void) | undefined;
  onBackToPractice?: (() => void) | undefined;
}

const DEFAULT_SUGGESTIONS = [
  "Explain it differently",
  "Give me a similar question",
  "Why are the other options wrong?",
];

/**
 * Screen 17 — the tutor, pinned to a question.
 *
 * The context strip stays visible because a tutor answer detached from its
 * question is unverifiable: a student cannot tell whether the explanation is
 * about the thing they got wrong.
 *
 * Free text is a collapsed pill by default. The suggested follow-ups are what
 * most students want and are cheaper to serve than an open prompt.
 */
export function TutorScreen({
  context,
  question,
  answer,
  suggestions = DEFAULT_SUGGESTIONS,
  onAsk,
  onBackToPractice,
}: TutorScreenProps) {
  const [draft, setDraft] = useState("");
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="app-column">
      <div className="flex-1 px-5 pb-6 pt-6">
        <p className="m-0 rounded-[var(--radius-control)] border border-border bg-surface px-3 py-2 text-[11.5px] leading-[1.45] text-text-muted">
          {context.subject} · {context.source} · Q{context.questionNumber} — {context.summary}
        </p>

        <QuestionText as="p" className="m-0 pt-4 text-[14px] font-semibold leading-[1.45]">
          {question}
        </QuestionText>

        <div className="mt-4 rounded-[var(--radius-card)] border border-border bg-surface p-4">
          <p className="m-0 text-[13.5px] leading-[1.6]">{answer}</p>
        </div>

        <ul className="m-0 flex list-none flex-col gap-2 p-0 pt-4">
          {suggestions.map((suggestion) => (
            <li key={suggestion}>
              <button
                type="button"
                onClick={() => onAsk?.(suggestion)}
                className="min-h-11 w-full rounded-[var(--radius-chip)] border border-border bg-surface px-3.5 py-2.5 text-left text-[13px]"
              >
                {suggestion}
              </button>
            </li>
          ))}
        </ul>

        <div className="pt-4">
          {expanded ? (
            <div className="flex flex-col gap-2">
              <label htmlFor="tutor-ask" className="sr-only">
                Ask something specific
              </label>
              <textarea
                id="tutor-ask"
                rows={3}
                autoFocus
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder="Ask something specific…"
                className="w-full resize-y rounded-[var(--radius-control)] border border-border-strong bg-surface p-3 text-[14px] text-text outline-none placeholder:text-text-muted focus:border-accent"
              />
              <Button
                className="text-[13px]"
                disabled={draft.trim().length === 0}
                onClick={() => {
                  onAsk?.(draft.trim());
                  setDraft("");
                }}
              >
                Ask
              </Button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="min-h-11 w-full rounded-[var(--radius-chip)] border border-border-strong bg-surface px-3.5 text-left text-[13px] text-text-muted"
            >
              Ask something specific…
            </button>
          )}
        </div>

        <div className="pt-5">
          <Button variant="ghost" onClick={onBackToPractice}>
            Back to practice
          </Button>
        </div>
      </div>

      <TabBar />
    </div>
  );
}
